/**
 * Pick the message to show for a failed HTTP response.
 *
 * Two error shapes reach us and they disagree about which field carries meaning:
 *
 * - the app's own errors (`replyFromDomainError`, `handleError`) send
 *   `{ error: "list_id is required when scope is 'list'" }`: `error` IS the reason;
 * - errors Fastify answers itself send `{ statusCode, code, error: "Bad Request",
 *   message: "body/profile_ids/0 must match format \"uuid\"" }`: `error` is the
 *   generic HTTP label and `message` is the reason.
 *
 * Reading `error ?? message` served the first shape and made the second one
 * unreachable: `error` is always present, so every validation failure surfaced as
 * "Bad Request". Hence the rule below: prefer the more *specific* field rather
 * than a fixed order.
 *
 * This is not `classifyInfraError` (`@/utils/infra-error`): that one classifies an
 * error already thrown as network vs API. This runs earlier, on the response body,
 * to decide what the thrown `ApiError` will say.
 */

/**
 * HTTP reason phrases. When `error` holds one of these, it is a label Fastify put
 * there, not something the app wrote, so `message`, if any, is more specific.
 */
const GENERIC_HTTP_LABELS = new Set([
  'Bad Request',
  'Unauthorized',
  'Payment Required',
  'Forbidden',
  'Not Found',
  'Method Not Allowed',
  'Conflict',
  'Payload Too Large',
  'Unsupported Media Type',
  'Unprocessable Entity',
  'Too Many Requests',
  'Internal Server Error',
  'Not Implemented',
  'Bad Gateway',
  'Service Unavailable',
  'Gateway Timeout',
]);

/**
 * ajv messages are prefixed with where the failure was found: `body/profile_ids/0
 * must match format "uuid"`, `body must have required property 'list_id'`. The
 * prefix is noise to a reader, the rest is the reason.
 */
const AJV_LOCATION_PREFIX = /^(?:body|querystring|params|headers)(?:\/|\s)/;

const nonEmptyString = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

export function httpErrorMessage(body: unknown, status: number, fallback: string): string {
  const source = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
  const error = nonEmptyString(source.error);
  const message = nonEmptyString(source.message);

  // The app wrote this one; it already says what happened.
  if (error && !GENERIC_HTTP_LABELS.has(error)) return error;

  // Never surface `message` on a server error: nothing on the backend shapes 5xx
  // bodies (see PI-78), so Fastify's default handler puts the raw internal
  // exception message there. That is not for the user's screen.
  if (status >= 500) return error ?? fallback;

  if (message) {
    return AJV_LOCATION_PREFIX.test(message)
      ? `Invalid request: ${message.replace(AJV_LOCATION_PREFIX, '')}`
      : message;
  }

  return error ?? fallback;
}
